/**
 * Helpers para sistema de peajes Chile 2026.
 * Normalización, cálculo y selección de tarifas.
 */

// ─── Normalización ────────────────────────────────────────────────────────────

export function slugifyId(value = '') {
  return String(value)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function generateTollDocId(item) {
  return [
    slugifyId(item.ruta),
    slugifyId(item.nombrePeaje),
    slugifyId(item.categoriaVehiculo),
    slugifyId(item.tipoTarifa),
    item.anio,
  ].join('-');
}

export function normalizeVehicleCategory(value = '') {
  const text = String(value)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();

  if (text.includes('moto')) return 'moto';
  if (text.includes('remolque')) return 'auto_con_remolque';
  if (text.includes('auto') || text.includes('camioneta') || text.includes('liviano')) return 'auto_camioneta';
  if (text.includes('bus')) return 'bus_2_ejes';
  if (text.includes('mas_2') || text.includes('mas 2') || text.includes('3 eje') || text.includes('+2')) return 'camion_mas_2_ejes';
  if (text.includes('2_eje') || text.includes('2 eje') || text.includes('camion_2') || text.includes('camion 2')) return 'camion_2_ejes';

  return 'auto_camioneta';
}

export function getVehicleTollCategory(vehicle) {
  const tipo = String(vehicle?.tipoVehiculo || '').toLowerCase();
  if (tipo.includes('moto')) return 'moto';
  if (tipo.includes('camion')) return 'camion_2_ejes';
  if (tipo.includes('bus')) return 'bus_2_ejes';
  return 'auto_camioneta';
}

export function normalizeTollRateType(value = '') {
  const text = String(value)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();

  if (text.includes('punta')) return 'punta';
  if (text.includes('saturacion')) return 'saturacion';
  if (text.includes('valle')) return 'valle';
  if (text.includes('festivo')) return 'festivo';
  if (text.includes('fin')) return 'fin_de_semana';

  return 'normal';
}

// ─── Reglas de horario ────────────────────────────────────────────────────────

export function isRateRuleActive(rule, travelDate = new Date()) {
  if (!rule?.aplicaHorario) return false;

  const dayName = new Intl.DateTimeFormat('es-CL', {
    weekday: 'long',
    timeZone: 'America/Santiago',
  }).format(travelDate).toLowerCase();

  const timeParts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Santiago',
  }).formatToParts(travelDate);

  const hour = Number(timeParts.find((p) => p.type === 'hour')?.value || 0);
  const minute = Number(timeParts.find((p) => p.type === 'minute')?.value || 0);
  const currentMinutes = hour * 60 + minute;

  const [startHour, startMinute] = String(rule.horarioInicio || '00:00').split(':').map(Number);
  const [endHour, endMinute] = String(rule.horarioFin || '23:59').split(':').map(Number);

  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  const matchesDay =
    !rule.diasAplica?.length ||
    rule.diasAplica.map((d) => String(d).toLowerCase()).includes(dayName);

  const matchesTime = currentMinutes >= startMinutes && currentMinutes <= endMinutes;

  return matchesDay && matchesTime;
}

// ─── Selección de tarifa aplicable ───────────────────────────────────────────

export function selectApplicableTollRate({
  tollRates = [],
  vehicleCategory = 'auto_camioneta',
  travelDate = new Date(),
}) {
  const sameCategory = tollRates.filter((r) => r.categoriaVehiculo === vehicleCategory);
  const fallback = tollRates.filter((r) => r.categoriaVehiculo === 'auto_camioneta');
  const candidates = sameCategory.length ? sameCategory : fallback;

  const activeSpecial = candidates.find(
    (r) =>
      ['punta', 'saturacion', 'festivo', 'fin_de_semana'].includes(r.tipoTarifa) &&
      isRateRuleActive(r.reglasTarifa, travelDate)
  );

  if (activeSpecial) {
    return {
      rate: activeSpecial,
      estimated: sameCategory.length === 0,
      reason: `Tarifa ${activeSpecial.tipoTarifa} aplicada según horario/regla.`,
    };
  }

  const normal = candidates.find((r) => r.tipoTarifa === 'normal') || candidates[0];

  return {
    rate: normal || null,
    estimated: sameCategory.length === 0,
    reason: normal ? 'Tarifa normal aplicada.' : 'No se encontró tarifa para este peaje.',
  };
}

// ─── Cálculo de viaje ─────────────────────────────────────────────────────────

export function calculateFuelCost({ distanceKm, performanceKmL, fuelPrice }) {
  const distance = Number(distanceKm || 0);
  const performance = Number(performanceKmL || 0);
  const price = Number(fuelPrice || 0);

  if (!distance || !performance || !price) {
    return { liters: 0, cost: 0, estimated: true };
  }

  const liters = distance / performance;
  const cost = liters * price;

  return {
    liters: Math.round(liters * 100) / 100,
    cost: Math.round(cost),
    estimated: false,
  };
}

export function calculateTollsCost({ selectedTolls = [], vehicleCategory, travelDate }) {
  const details = [];
  let total = 0;
  let hasEstimated = false;

  selectedTolls.forEach((tollGroup) => {
    const result = selectApplicableTollRate({
      tollRates: tollGroup.rates,
      vehicleCategory,
      travelDate,
    });

    if (result.rate) {
      const amount = Number(result.rate.tarifa2026 || 0);
      total += amount;
      if (result.estimated) hasEstimated = true;
      details.push({
        peaje: result.rate.nombrePeaje,
        ruta: result.rate.ruta,
        km: result.rate.km,
        tipoTarifa: result.rate.tipoTarifa,
        tarifa: amount,
        estimated: result.estimated,
        reason: result.reason,
      });
    } else {
      hasEstimated = true;
    }
  });

  return {
    total,
    details,
    estimated: hasEstimated || details.some((d) => d.estimated),
  };
}

// ─── Sugerencia de ruta ───────────────────────────────────────────────────────

export function suggestRoutesByOriginDestination({ originText = '', destinationText = '' }) {
  const dest = String(destinationText).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

  if (
    dest.includes('valparaiso') || dest.includes('vina') || dest.includes('vina del mar') ||
    dest.includes('algarrobo') || dest.includes('casablanca') || dest.includes('quintero')
  ) {
    return ['Ruta 68'];
  }
  if (
    dest.includes('san antonio') || dest.includes('melipilla') ||
    dest.includes('el quisco') || dest.includes('santo domingo') || dest.includes('cartagena')
  ) {
    return ['Ruta 78'];
  }
  if (dest.includes('los andes') || dest.includes('mendoza')) {
    return ['Ruta 57'];
  }
  if (
    dest.includes('rancagua') || dest.includes('talca') || dest.includes('chillan') ||
    dest.includes('concepcion') || dest.includes('temuco') || dest.includes('osorno') ||
    dest.includes('puerto montt') || dest.includes('pto montt') || dest.includes('pto. montt') ||
    dest.includes('valdivia') || dest.includes('los angeles')
  ) {
    return ['Ruta 5 Sur'];
  }
  if (
    dest.includes('la serena') || dest.includes('coquimbo') || dest.includes('illapel') ||
    dest.includes('copiapo') || dest.includes('calama') || dest.includes('antofagasta') ||
    dest.includes('iquique') || dest.includes('arica') || dest.includes('ovalle')
  ) {
    return ['Ruta 5 Norte'];
  }

  return [];
}

// ─── Agrupación ───────────────────────────────────────────────────────────────

export function groupTollRatesByPlaza(records = []) {
  const map = new Map();

  records.forEach((record) => {
    const key = [record.ruta, record.nombrePeaje, record.km ?? ''].join('|');

    if (!map.has(key)) {
      map.set(key, {
        id: key,
        ruta: record.ruta,
        tramo: record.tramo,
        nombrePeaje: record.nombrePeaje,
        km: record.km,
        region: record.region,
        comuna: record.comuna,
        tipoPeaje: record.tipoPeaje,
        concesionaria: record.concesionaria,
        ordenNorteSur: record.ordenNorteSur,
        ordenRuta: record.ordenRuta,
        lat: record.lat || null,
        lng: record.lng || null,
        rates: [],
      });
    }

    map.get(key).rates.push(record);
  });

  return Array.from(map.values()).sort(
    (a, b) => (Number(a.ordenNorteSur) || 99) - (Number(b.ordenNorteSur) || 99) ||
              (Number(a.ordenRuta) || 99) - (Number(b.ordenRuta) || 99)
  );
}

// ─── Normalización de registro CSV ───────────────────────────────────────────

const TOLL_DEFAULTS = {
  pais: 'CL',
  anio: 2026,
  moneda: 'CLP',
  categoriaVehiculo: 'auto_camioneta',
  tipoTarifa: 'normal',
  tipoPeaje: 'troncal',
  sentido: 'ambos',
  activo: true,
  estado: 'base_validacion',
  fechaActualizacion: '2026-02-01',
  vigenteDesde: '2026-01-01',
  vigenteHasta: '2026-12-31',
  fuente: 'Chocale.cl',
  fuenteNombre: 'Peajes 2026',
  fuenteUrl: 'https://chocale.cl/2026/02/peajes-2026-conoce-las-tarifas-para-transitar-por-las-carreteras-este-ano/',
  reglasTarifa: {
    aplicaHorario: false,
    diasAplica: [],
    horarioInicio: '',
    horarioFin: '',
    aplicaFeriados: false,
    aplicaFinSemana: false,
    descripcion: '',
  },
};

export function normalizeTollRecord(row, overrideDefaults = {}) {
  const defaults = { ...TOLL_DEFAULTS, ...overrideDefaults };

  const tarifa2026 = Number(String(row.tarifa2026 || '').replace(/[^0-9.-]/g, '')) || 0;
  const tarifa2025 = Number(String(row.tarifa2025 || '').replace(/[^0-9.-]/g, '')) || null;
  const variacion = tarifa2025 && tarifa2026
    ? Math.round(((tarifa2026 - tarifa2025) / tarifa2025) * 10000) / 100
    : null;

  const lat = Number(row.lat) || null;
  const lng = Number(row.lng) || null;
  const km = row.km !== '' && row.km !== undefined ? Number(row.km) || null : null;

  const categoriaVehiculo = row.categoriaVehiculo
    ? normalizeVehicleCategory(row.categoriaVehiculo)
    : defaults.categoriaVehiculo;

  const tipoTarifa = row.tipoTarifa
    ? normalizeTollRateType(row.tipoTarifa)
    : defaults.tipoTarifa;

  const activo = row.activo === 'FALSE' || row.activo === false ? false : true;

  const record = {
    pais: row.pais || defaults.pais,
    anio: Number(row.anio) || defaults.anio,
    ruta: row.ruta || '',
    tramo: row.tramo || '',
    nombrePeaje: row.nombrePeaje || '',
    tipoPeaje: row.tipoPeaje || defaults.tipoPeaje,
    concesionaria: row.concesionaria || '',
    region: row.region || '',
    comuna: row.comuna || '',
    km,
    sentido: row.sentido || defaults.sentido,
    lat: lat && Math.abs(lat) < 90 ? lat : null,
    lng: lng && Math.abs(lng) < 180 ? lng : null,
    categoriaVehiculo,
    tipoTarifa,
    tarifa2026,
    tarifa2025,
    variacionPorcentaje: variacion,
    moneda: row.moneda || defaults.moneda,
    fechaActualizacion: row.fechaActualizacion || defaults.fechaActualizacion,
    vigenteDesde: row.vigenteDesde || defaults.vigenteDesde,
    vigenteHasta: row.vigenteHasta || defaults.vigenteHasta,
    ordenNorteSur: Number(row.ordenNorteSur) || null,
    ordenRuta: Number(row.ordenRuta) || null,
    fuente: row.fuente || defaults.fuente,
    fuenteNombre: row.fuenteNombre || defaults.fuenteNombre,
    fuenteUrl: row.fuenteUrl || defaults.fuenteUrl,
    estado: row.estado || defaults.estado,
    activo,
    notas: row.notas || '',
    reglasTarifa: { ...defaults.reglasTarifa },
  };

  // Mark special tariffs without time rules
  if (['punta', 'saturacion', 'festivo', 'fin_de_semana'].includes(record.tipoTarifa)) {
    if (!record.reglasTarifa.aplicaHorario && !record.notas?.includes('regla')) {
      record.notas = [record.notas, 'Falta regla horaria para tarifa especial.']
        .filter(Boolean).join(' ');
    }
  }

  return record;
}

// ─── Validación ───────────────────────────────────────────────────────────────

export function validateTollRecord(record) {
  const errors = [];
  if (!record.ruta) errors.push('ruta es obligatoria');
  if (!record.nombrePeaje) errors.push('nombrePeaje es obligatorio');
  if (!record.anio) errors.push('anio es obligatorio');
  if (!record.categoriaVehiculo) errors.push('categoriaVehiculo es obligatorio');
  if (!record.tipoTarifa) errors.push('tipoTarifa es obligatorio');
  if (record.tarifa2026 === undefined || record.tarifa2026 === null || record.tarifa2026 < 0) {
    errors.push('tarifa2026 debe ser mayor o igual a 0');
  }
  return { valid: errors.length === 0, errors };
}

// ─── Formato CLP ─────────────────────────────────────────────────────────────

export function formatCLP(amount) {
  if (!amount && amount !== 0) return '-';
  return '$' + Number(amount).toLocaleString('es-CL');
}

// ─── Badge de estado ─────────────────────────────────────────────────────────

export const TOLL_ESTADO_BADGE = {
  oficial:               { label: 'Oficial',             cls: 'bg-emerald-100 text-emerald-700' },
  base_validacion:       { label: 'Base',                cls: 'bg-blue-100 text-blue-700'      },
  pendiente_validacion:  { label: 'Pendiente',           cls: 'bg-amber-100 text-amber-700'    },
  estimado:              { label: 'Estimado',            cls: 'bg-amber-100 text-amber-700'    },
  desactualizado:        { label: 'Desactualizado',      cls: 'bg-red-100 text-red-700'        },
};

export const TOLL_TARIFA_BADGE = {
  normal:        { label: 'Normal',      cls: 'bg-slate-100 text-slate-700'   },
  punta:         { label: 'Punta',       cls: 'bg-orange-100 text-orange-700' },
  saturacion:    { label: 'Saturación',  cls: 'bg-red-100 text-red-700'       },
  festivo:       { label: 'Festivo',     cls: 'bg-violet-100 text-violet-700' },
  fin_de_semana: { label: 'Fin semana',  cls: 'bg-blue-100 text-blue-700'     },
  valle:         { label: 'Valle',       cls: 'bg-green-100 text-green-700'   },
};
